import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { 
  Search, MapPin, Stethoscope, Phone, Download, 
  Database, Users, Building, QrCode, Share2, RefreshCw, UserPlus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Sidebar } from "./Dashboard";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const ProviderSearch = () => {
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  
  const [npiProviders, setNpiProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("Dallas");
  const [showQRModal, setShowQRModal] = useState(false);
  const [stats, setStats] = useState({ total: 0 });

  const fetchImportedProviders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      params.append("limit", "100");
      
      const response = await axios.get(`${API}/npi/providers?${params}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setNpiProviders(response.data.providers);
      setStats({ total: response.data.total });
    } catch (error) {
      toast.error("Unable to load providers");
    } finally {
      setLoading(false);
    }
  }, [search, getAuthHeaders]);

  useEffect(() => {
    fetchImportedProviders();
  }, [fetchImportedProviders]);

  const handleImportFromNPI = async () => {
    setImporting(true);
    try {
      const response = await axios.post(`${API}/npi/import?city=${city}&state=TX&limit=500`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(`Imported ${response.data.imported} providers from ${city}!`);
      fetchImportedProviders();
    } catch (error) {
      toast.error("Failed to import providers");
    } finally {
      setImporting(false);
    }
  };

  const handleSearchNPI = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("city", city);
      params.append("state", "TX");
      if (search) params.append("specialty", search);
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/npi/search?${params}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setNpiProviders(response.data.providers);
      setStats({ total: response.data.total });
    } catch (error) {
      toast.error("Failed to search NPI Registry");
    } finally {
      setLoading(false);
    }
  };

  const appUrl = window.location.origin + "/register";

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <Sidebar currentPath={location.pathname} />
      
      <main className="pt-20 lg:pt-8 p-4 lg:p-8" data-testid="provider-search-main">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Provider Search
              </h1>
              <p className="text-slate-600">Search and import providers from the NPI Registry by city/state</p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => setShowQRModal(true)}
                data-testid="show-qr-btn"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Share QR Code
              </Button>
              <Button 
                onClick={handleImportFromNPI}
                disabled={importing}
                className="bg-primary text-white hover:bg-primary/90"
                data-testid="import-npi-btn"
              >
                {importing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Import from NPI
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-5 h-5 text-primary" />
                <span className="text-sm text-slate-500">Imported</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-500">Registered</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {npiProviders.filter(p => p.is_registered).length}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building className="w-5 h-5 text-amber-500" />
                <span className="text-sm text-slate-500">Coverage</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">Nationwide</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Stethoscope className="w-5 h-5 text-purple-500" />
                <span className="text-sm text-slate-500">Source</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">NPI</p>
            </div>
          </div>

          {/* Search */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search by name or specialty..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="provider-search-input"
                />
              </div>
              <Button onClick={handleSearchNPI} variant="outline">
                <Search className="w-4 h-4 mr-2" />
                Search NPI
              </Button>
            </div>
          </div>

          {/* Provider List */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-3/4 mb-3"></div>
                  <div className="h-3 bg-slate-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : npiProviders.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {npiProviders.map((provider, index) => (
                <motion.div
                  key={provider.npi || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {provider.name}
                        {provider.credential && (
                          <span className="text-slate-500 font-normal">, {provider.credential}</span>
                        )}
                      </h3>
                      <p className="text-primary text-sm">{provider.specialty || 'Healthcare Provider'}</p>
                    </div>
                    {provider.is_registered ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Registered
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-50 text-slate-600 border border-slate-200">
                        NPI
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-2 text-sm text-slate-600">
                    {provider.address && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                        <span>
                          {provider.address}
                          {provider.city && `, ${provider.city}`}
                          {provider.state && `, ${provider.state}`}
                          {provider.zip_code && ` ${provider.zip_code}`}
                        </span>
                      </div>
                    )}
                    {provider.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{provider.phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-400">NPI: {provider.npi}</span>
                    {!provider.is_registered && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => {
                          const subject = encodeURIComponent("Join iConcierge Healthcare Marketing");
                          const body = encodeURIComponent(
                            `Hi ${provider.name?.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')},\n\nI'd like to invite you to join the iConcierge Healthcare Marketing platform where healthcare professionals connect, schedule meetings, and manage referrals.\n\nYou can claim your profile and get started here:\nhttps://iconciergehm.com/browse\n\nLooking forward to connecting!\n`
                          );
                          window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                          toast.success("Opening email to invite provider");
                        }}
                        data-testid={`invite-btn-${provider.npi}`}
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        Invite to Join
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <Database className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No providers found</h3>
              <p className="text-slate-600 mb-6">
                Import providers from the NPI Registry to get started
              </p>
              <Button onClick={handleImportFromNPI} disabled={importing} className="bg-primary text-white">
                <Database className="w-4 h-4 mr-2" />
                Import Providers from NPI
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* QR Code Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Share iConcierge</DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col items-center py-6">
            <div className="bg-white p-4 rounded-2xl shadow-lg border border-slate-200 mb-6">
              <QRCodeSVG 
                value={appUrl}
                size={200}
                level="H"
                fgColor="#0284C7"
                imageSettings={{
                  src: "https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg",
                  height: 40,
                  width: 40,
                  excavate: true
                }}
              />
            </div>
            
            <p className="text-center text-slate-600 mb-4">
              Scan this QR code to join iConcierge and connect with healthcare providers
            </p>

            <div className="w-full p-3 bg-slate-50 rounded-lg text-center text-sm text-slate-600 break-all">
              {appUrl}
            </div>

            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(appUrl);
                  toast.success("Link copied!");
                }}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button 
                className="bg-primary text-white"
                onClick={() => {
                  // Download QR code
                  const svg = document.querySelector('.qr-code-svg');
                  if (svg) {
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const img = new Image();
                    img.onload = () => {
                      canvas.width = img.width;
                      canvas.height = img.height;
                      ctx.drawImage(img, 0, 0);
                      const link = document.createElement("a");
                      link.download = "iconcierge-qr.png";
                      link.href = canvas.toDataURL("image/png");
                      link.click();
                    };
                    img.src = "data:image/svg+xml;base64," + btoa(svgData);
                  }
                  toast.success("QR Code downloaded!");
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderSearch;
